import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, User, Calendar } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
    api.get("/articles").then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 4)));
  }, [slug]);

  if (notFound) return <div className="py-32 text-center text-slate-400">Article not found. <Link to="/knowledge" className="text-navy underline">Back to Knowledge Center</Link></div>;
  if (!article) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  const date = new Date(article.date).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid="article-detail-page">
      <PageHero eyebrow={article.category} title={article.title} crumbs={[{ label: "Knowledge Center", to: "/knowledge" }, { label: article.title }]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 pb-6 border-b border-border">
          <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-gold" />{article.author}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gold" />{date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" />{article.reading_time}</span>
        </div>
        <p className="mt-6 text-lg text-slate-700 leading-relaxed font-medium">{article.excerpt}</p>
        <article className="mt-6 space-y-5">
          {article.content.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed text-[15px]">{p}</p>)}
        </article>
        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {article.tags.map((t) => <span key={t} className="text-xs px-3 py-1 rounded-md bg-subtle border border-border text-slate-600">{t}</span>)}
          </div>
        )}
        {article.sources?.length > 0 && (
          <p className="mt-6 text-xs text-slate-400">Sources: {article.sources.join(", ")}</p>
        )}
        <div className="mt-8"><Disclaimer compact /></div>

        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="font-display text-2xl font-bold text-navy mb-6">Related Articles</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/knowledge/${r.slug}`} className="p-4 border border-border rounded-md hover:bg-subtle transition-colors" data-testid={`related-article-${r.slug}`}>
                  <p className="eyebrow text-gold mb-1">{r.category}</p>
                  <p className="text-sm font-medium text-navy">{r.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
